import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { getArrayTableColumns, isArrayTableViewable } from '../utils/treeBuilder';

/**
 * 格式化基本类型的值
 */
function formatPrimitiveValue(value, type) {
  if (value === null) return 'null';
  if (value === undefined) return '';
  if (type === 'string') return `"${value}"`;
  return String(value);
}

/**
 * 判断是否为基本类型（用于智能展开判断）
 */
function isPrimitive(value) {
  if (value === null || value === undefined) return true;
  const type = typeof value;
  return type === 'string' || type === 'number' || type === 'boolean';
}

/**
 * 判断数组/对象是否应该折叠
 */
function shouldCollapse(value, depth) {
  // 深度 = 0（顶层表格单元格）
  if (depth === 0) {
    if (Array.isArray(value)) {
      // 空数组 → 不折叠
      if (value.length === 0) return false;

      // 小数组（≤ 5）且都是基本类型 → 不折叠（行内展示）
      if (value.length <= 5 && value.every(isPrimitive)) return false;

      // 大数组或复杂数组 → 折叠
      return true;
    }

    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value);

      // 空对象 → 不折叠
      if (entries.length === 0) return false;

      // 小对象（≤ 3 字段）且所有值都是基本类型 → 不折叠（行内展示）
      if (entries.length <= 3 && entries.every(([k, v]) => isPrimitive(v))) return false;

      // 大对象或复杂对象 → 折叠
      return true;
    }
  }

  // 深度 > 0（嵌套层级）
  // 所有非空数组/对象都折叠（但可以展开）
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value).length > 0;
  }

  return false;
}

/**
 * 尝试解析 JSON 字符串（与 treeBuilder.js 中的逻辑一致）
 */
function tryParseJSONString(value, maxSize = 10240) {
  if (typeof value !== 'string') return null;
  if (value.length > maxSize) return null;

  const trimmed = value.trim();
  if (!(
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  )) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'object' && parsed !== null) {
      return { parsed, originalString: value };
    }
  } catch (e) {
    return null;
  }

  return null;
}

/**
 * 行内展示小数组（基本类型元素）
 */
function InlineArray({ value }) {
  return (
    <span className="text-indigo-600 dark:text-indigo-400 font-mono text-sm">
      [
      {value.map((item, index) => (
        <span key={index}>
          {index > 0 && <span className="text-gray-400">, </span>}
          <span className={isPrimitive(item) ? getInlineValueColor(item) : ''}>
            {formatPrimitiveValue(item, typeof item)}
          </span>
        </span>
      ))}
      ]
    </span>
  );
}

/**
 * 行内展示小对象（基本类型值）
 */
function InlineObject({ value }) {
  const entries = Object.entries(value);
  return (
    <span className="text-purple-600 dark:text-purple-400 font-mono text-sm">
      {'{'}
      {entries.map(([key, val], index) => (
        <span key={key}>
          {index > 0 && <span className="text-gray-400">, </span>}
          <span className="text-fuchsia-600 dark:text-fuchsia-400">{key}</span>
          <span className="text-gray-400">: </span>
          <span className={getInlineValueColor(val)}>
            {formatPrimitiveValue(val, typeof val)}
          </span>
        </span>
      ))}
      {'}'}
    </span>
  );
}

/**
 * 获取行内值的颜色
 */
function getInlineValueColor(value) {
  const type = typeof value;
  if (type === 'string') return 'text-green-600 dark:text-green-400';
  if (type === 'number') return 'text-blue-600 dark:text-blue-400';
  if (type === 'boolean') return 'text-orange-600 dark:text-orange-400';
  if (value === null) return 'text-gray-400 dark:text-gray-500 italic';
  return 'text-gray-800 dark:text-gray-200';
}

/**
 * 可折叠数组展示组件（用于基本类型数组或深层嵌套）
 */
function CollapsibleArray({ value, depth }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
        title="点击展开"
      >
        Array[{value.length}]
        <ChevronRight size={14} />
      </button>
    );
  }

  // 已展开 - 垂直列表展示
  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={() => setIsExpanded(false)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors self-start"
      >
        <ChevronDown size={14} />
        Array[{value.length}]
      </button>
      <div className="ml-4 pl-3 border-l-2 border-indigo-200 dark:border-indigo-700">
        <div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700">
          {value.map((item, index) => (
            <div key={index} className="flex gap-3 items-start py-1.5 first:pt-0 last:pb-0">
              <span className="text-gray-500 dark:text-gray-400 font-semibold min-w-[24px] text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                {index}
              </span>
              <div className="flex-1">
                <CellContent value={item} depth={depth + 1} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * 可折叠对象数组展示组件（嵌套表格）
 */
function CollapsibleObjectArray({ value, depth }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
        title="点击展开嵌套表格"
      >
        Array[{value.length}]
        <ChevronRight size={14} />
      </button>
    );
  }

  // 已展开 - 渲染嵌套表格（横向！）
  return (
    <div className="flex flex-col gap-2 mt-2">
      <button
        onClick={() => setIsExpanded(false)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors self-start"
      >
        <ChevronDown size={14} />
        Array[{value.length}]
      </button>
      {/* 嵌套表格 - 横向展示 */}
      <div className="ml-4">
        <ArrayTableView data={value} level={depth + 1} />
      </div>
    </div>
  );
}

/**
 * 可折叠对象展示组件
 */
function CollapsibleObject({ value, depth }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const entries = Object.entries(value);

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
        title="点击展开"
      >
        Object{'{'}{entries.length}{'}'}
        <ChevronRight size={14} />
      </button>
    );
  }

  // 已展开 - 键值对列表
  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={() => setIsExpanded(false)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors self-start"
      >
        <ChevronDown size={14} />
        Object{'{'}{entries.length}{'}'}
      </button>
      <div className="ml-4 pl-3 border-l-2 border-purple-200 dark:border-purple-700">
        <div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700">
          {entries.map(([key, val]) => (
            <div key={key} className="flex gap-3 items-start py-1.5 first:pt-0 last:pb-0">
              <span className="text-gray-700 dark:text-gray-300 font-semibold min-w-[100px] text-xs bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded">
                {key}
              </span>
              <div className="flex-1">
                <CellContent value={val} depth={depth + 1} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * 渲染单元格内容（智能展开 + 字符串解析 + 深度限制）
 */
function CellContent({ value, depth = 0 }) {
  // null 和 undefined
  if (value === null) {
    return <span className="text-gray-400 dark:text-gray-500 italic">null</span>;
  }
  if (value === undefined) {
    return <span className="text-gray-300 dark:text-gray-600"></span>;
  }

  // 数组处理
  if (Array.isArray(value)) {
    // 空数组 → 直接显示 []
    if (value.length === 0) {
      return <span className="text-indigo-600 dark:text-indigo-400">[]</span>;
    }

    // 判断是否为对象数组（可以用表格展示）
    const isObjectArray = isArrayTableViewable(value);

    if (isObjectArray) {
      // 对象数组 → 嵌套表格（横向！）
      // 深度限制：depth >= 8 时只显示按钮
      if (depth >= 8) {
        return (
          <button
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
            title="深度限制，无法继续展开"
            disabled
          >
            Array[{value.length}]
          </button>
        );
      }

      // depth < 8 → 可以展开为嵌套表格
      return <CollapsibleObjectArray value={value} depth={depth} />;
    }

    // 非对象数组（基本类型数组）
    // 使用智能判断
    if (shouldCollapse(value, depth)) {
      // 需要折叠 → 垂直列表
      return <CollapsibleArray value={value} depth={depth} />;
    } else {
      // 不需要折叠 → 行内展示
      return <InlineArray value={value} />;
    }
  }

  // 对象处理
  if (typeof value === 'object') {
    const entries = Object.entries(value);

    // 空对象 → 直接显示 {}
    if (entries.length === 0) {
      return <span className="text-purple-600 dark:text-purple-400">{'{}'}</span>;
    }

    // 使用智能判断
    if (shouldCollapse(value, depth)) {
      // 需要折叠 → 键值对列表
      return <CollapsibleObject value={value} depth={depth} />;
    } else {
      // 不需要折叠 → 行内展示
      return <InlineObject value={value} />;
    }
  }

  // 基本类型 - 特殊处理字符串（尝试 JSON 解析）
  const type = typeof value;

  // 字符串类型：尝试解析（限制深度为 8 层，支持深度嵌套）
  if (type === 'string' && depth < 8) {
    const parseResult = tryParseJSONString(value);
    if (parseResult) {
      // 解析成功！递归渲染
      const originalPreview = parseResult.originalString.length > 100
        ? parseResult.originalString.substring(0, 100) + '...'
        : parseResult.originalString;

      return (
        <div className="flex flex-col gap-2">
          {/* 解析标记 */}
          <div className="flex items-center gap-2">
            <span
              className="text-xs text-blue-500 dark:text-blue-400 flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded"
              title={`原始字符串: ${originalPreview}`}
            >
              🔗 <span>从字符串解析</span>
            </span>
          </div>
          {/* 递归渲染解析后的内容 */}
          <div className="ml-4 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
            <CellContent value={parseResult.parsed} depth={depth + 1} />
          </div>
        </div>
      );
    }
  }

  // 普通基本类型（支持深色模式）
  let colorClass = 'text-gray-800 dark:text-gray-200';
  if (type === 'string') colorClass = 'text-green-600 dark:text-green-400';
  else if (type === 'number') colorClass = 'text-blue-600 dark:text-blue-400';
  else if (type === 'boolean') colorClass = 'text-orange-600 dark:text-orange-400';

  return (
    <span className={colorClass}>
      {formatPrimitiveValue(value, type)}
    </span>
  );
}


/**
 * 数组表格视图组件（类似 Postman 的表格展示）
 * @param {Object} props
 * @param {Array} props.data - 要展示的数组数据
 * @param {number} props.level - 当前层级（用于缩进）
 */
function ArrayTableView({ data, level = 0 }) {
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }

  const columns = getArrayTableColumns(data);

  return (
    <div
      className="overflow-auto max-h-[calc(100vh-200px)] border-l-4 border-indigo-300 dark:border-indigo-700 ml-4 mb-3 shadow-sm"
      style={{ marginLeft: `${level * 20 + 16}px` }}
    >
      <table className="min-w-full border-collapse text-sm bg-white dark:bg-gray-800">
        {/* 表头 */}
        <thead className="sticky top-0 z-20">
          <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border-b-2 border-gray-300 dark:border-gray-600">
            <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-bold text-gray-600 dark:text-gray-300 sticky left-0 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 shadow-sm z-30">
              #
            </th>
            {columns.map(col => (
              <th
                key={col}
                className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-bold text-gray-600 dark:text-gray-300 whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>

        {/* 表体 */}
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={`
                transition-colors duration-150
                ${rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}
                hover:bg-blue-50 dark:hover:bg-blue-900/20
              `}
            >
              <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300 font-semibold sticky left-0 bg-inherit">
                {rowIndex}
              </td>
              {columns.map(col => {
                const value = row[col];
                return (
                  <td
                    key={col}
                    className="border border-gray-300 dark:border-gray-600 px-4 py-3 font-mono align-top"
                  >
                    <CellContent value={value} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ArrayTableView;
